import { goalHeadline, goalScopeLine } from '../../utils/goalLabel';
import React, { useEffect, useMemo, useState } from 'react';
import ChildSelectionModal from '../../components/ChildSelectionModal';
import ChildSwitcherButton from '../../components/ChildSwitcherButton';
import { useChild } from '../../contexts/ChildContext';
import { parentApi } from '../../services/api';
import { goalAuthorBadge } from '../../utils/goalAuthor';
import { goalCountsLms } from '../../utils/goalCard';
import { AutoMark } from '../../components/GoalMarks';

/**
 * Parent → "Öğrenci Çalışma Planı" page.
 *
 * Read-only mirror of the student "Çalışma Planım · Program Görünümü": the
 * parent sees the week navigator, the weekly/daily ilerleme progress bars, and
 * the day grid — but cannot write (no Hedef Ata, no edit, no delete, no
 * completion toggle; the teacher owns is_completed). No Hedef Görünümü tab.
 *
 * Backed by /parent/children/:studentId/program which delegates to the shared
 * student schedule + goals services with parent-ownership gating.
 */

const F = { fontFamily: 'Inter' } as const;
const DAY_NAMES = ['Pazartesi', 'Salı', 'Çarşamba', 'Perşembe', 'Cuma', 'Cumartesi', 'Pazar'];

// Week navigator — Monday-anchored, identical math to the student screen.
function weekStartForOffset(offsetWeeks: number): Date {
  const d = new Date();
  const jsDow = d.getDay();
  const diff = (jsDow + 6) % 7;
  d.setDate(d.getDate() - diff + offsetWeeks * 7);
  d.setHours(0, 0, 0, 0);
  return d;
}
function formatWeekRange(monday: Date): string {
  const sunday = new Date(monday);
  sunday.setDate(sunday.getDate() + 6);
  const fmt = (x: Date) => `${x.getDate()} ${x.toLocaleDateString('tr-TR', { month: 'short' })}`;
  return `${fmt(monday)} – ${fmt(sunday)}`;
}
const toLocalIso = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

interface ScheduleEntry {
  id: number | string;
  day_of_week: number;
  start_time?: string | null;
  end_time?: string | null;
  title?: string | null;
  topic?: string | null;
  subject_id?: number | null;
  question_target?: number | null;
  target_questions?: number | null;
  duration_minutes?: number | null;
  subjects?: { id?: number; name?: string } | null;
  is_completed?: boolean;
  __isGoal?: boolean;
  __goalId?: string;
}

interface Goal {
  id: number | string | bigint;
  target_value?: number;
  achieved_value?: number;
  start_time?: string | null;
  end_time?: string | null;
  goal_date?: string | null;
  status?: string;
  period_type?: string;
  created_by_type?: string;
  created_by_name?: string | null; // the assigning teacher's name (badge)
  title?: string | null;            // the goal's typed name (2026-10)
  tracking_mode?: string | null;    // lms | manual | both — ⚡ when the LMS counts it
  auto_tracked?: boolean;
  goal_definitions?: { title?: string; subjects?: { id?: number; name?: string } | null };
  subject_name?: string | null;
  subject_units?: { title?: string; subjects?: { name?: string } | null } | null;
}

const fmtTimeRange = (start?: string | null, end?: string | null): string => {
  if (!start && !end) return '-';
  const f = (s?: string | null) => {
    if (!s) return '-';
    const d = new Date(s);
    return d.getUTCHours().toString().padStart(2, '0') + ':' + d.getUTCMinutes().toString().padStart(2, '0');
  };
  return `${f(start)} - ${f(end)}`;
};

const computeDuration = (start?: string | null, end?: string | null): number | null => {
  if (!start || !end) return null;
  const s = new Date(start);
  const e = new Date(end);
  const mins = Math.round((e.getTime() - s.getTime()) / 60000);
  return mins > 0 ? mins : null;
};

const ParentProgram: React.FC = () => {
  const { selectedChild, loading: childLoading } = useChild();
  const [schedules, setSchedules] = useState<any[]>([]);
  const [goals, setGoals] = useState<Goal[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Read-only week navigator (past / current / future all reachable).
  const [weekOffset, setWeekOffset] = useState(0);

  useEffect(() => {
    if (!selectedChild) return;
    setLoading(true); setError(null);
    parentApi.childProgram(selectedChild.id)
      .then((r: any) => {
        setSchedules(r.data?.schedule ?? []);
        setGoals(r.data?.goals ?? []);
      })
      .catch((e: any) => setError(e?.response?.data?.error ?? e.message))
      .finally(() => setLoading(false));
  }, [selectedChild]);

  const weekStart = useMemo(() => weekStartForOffset(weekOffset), [weekOffset]);
  const weekDays = useMemo(() => Array.from({ length: 7 }, (_, i) => {
    const d = new Date(weekStart);
    d.setDate(d.getDate() + i);
    return d;
  }), [weekStart]);
  const weekRangeLabel = formatWeekRange(weekStart);

  const todayLocal = new Date();
  const todayIso = toLocalIso(todayLocal);
  const isTodayInWeek = weekDays.some(d => toLocalIso(d) === todayIso);

  const allEntries: ScheduleEntry[] = useMemo(
    () => schedules.flatMap((s: any) => s.study_schedule_entries ?? []),
    [schedules],
  );

  // Read-only completion — derived from each entry's is_completed flag (the
  // teacher writes it). Keyed by local ISO date, same shape as the student.
  const attended: Record<string, Set<string>> = useMemo(() => {
    const out: Record<string, Set<string>> = {};
    for (const d of weekDays) {
      const iso = toLocalIso(d);
      const dow = (d.getDay() + 6) % 7;
      const ids = new Set<string>();
      for (const e of allEntries) {
        if (e.day_of_week === dow && e.is_completed) ids.add(String(e.id));
      }
      out[iso] = ids;
    }
    return out;
  }, [allEntries, weekDays]);

  // Schedule entries by day-of-week (repeat weekly) + goals mapped to their
  // weekday. Goals are date-filtered at render so navigating weeks re-scopes
  // them; schedule entries always show.
  const entriesByDay = useMemo(() => {
    const out: Record<number, ScheduleEntry[]> = {};
    for (let i = 0; i < 7; i++) out[i] = [];
    for (const e of allEntries) {
      if (e.day_of_week >= 0 && e.day_of_week <= 6) out[e.day_of_week].push(e);
    }
    const dedup = new Set<string>();
    for (const g of goals) {
      if (!g.goal_date) continue;
      const hasTimes = !!g.start_time && !!g.end_time;
      const isMultiDay = g.period_type === 'weekly' || g.period_type === 'monthly';
      if (!hasTimes && isMultiDay) continue; // period bands aren't shown on the parent grid
      const gd = new Date(g.goal_date);
      const dowIdx = (gd.getUTCDay() + 6) % 7;
      const title = goalHeadline(g);
      // By id: goals are no longer one-per-Hedef-Türü, and the week filter runs
      // at render time — keying by weekday+time+title hid a weekly repeat of
      // the same named goal on every week after the first.
      const dedupKey = `id:${g.id}`;
      if (dedup.has(dedupKey)) continue;
      dedup.add(dedupKey);
      out[dowIdx].push({
        id: `goal-${g.id}`,
        day_of_week: dowIdx,
        start_time: typeof g.start_time === 'string' ? g.start_time : null,
        end_time: typeof g.end_time === 'string' ? g.end_time : null,
        title,
        question_target: g.target_value,
        subjects: g.goal_definitions?.subjects ?? null,
        is_completed: g.status === 'completed',
        __isGoal: true,
        __goalId: String(g.id),
      });
    }
    for (const k of Object.keys(out)) {
      out[Number(k)].sort((a, b) => {
        const at = a.start_time ? new Date(a.start_time).getTime() : 0;
        const bt = b.start_time ? new Date(b.start_time).getTime() : 0;
        return at - bt;
      });
    }
    return out;
  }, [allEntries, goals]);

  // Weekly progress across the selected week's goals + schedule entries.
  const weekProgress = useMemo(() => {
    let total = 0, done = 0, qTarget = 0, qDone = 0;
    for (const g of goals) {
      if (!g.goal_date) continue;
      const iso = String(g.goal_date).slice(0, 10);
      if (!weekDays.some(d => toLocalIso(d) === iso)) continue;
      total++;
      const achieved = g.achieved_value ?? 0;
      const target = g.target_value ?? 0;
      if (target > 0 && achieved >= target) done++;
      if (target > 0) { qTarget += target; qDone += Math.min(achieved, target); }
    }
    for (const d of weekDays) {
      const iso = toLocalIso(d);
      const dow = (d.getDay() + 6) % 7;
      for (const e of allEntries) {
        if (e.day_of_week !== dow) continue;
        total++;
        if (attended[iso]?.has(String(e.id))) done++;
      }
    }
    return {
      total, done,
      pct: total > 0 ? Math.round((done / total) * 100) : 0,
      qTarget, qDone,
      qPct: qTarget > 0 ? Math.round((qDone / qTarget) * 100) : 0,
    };
  }, [goals, weekDays, allEntries, attended]);

  const dailyProgress = useMemo(() => {
    if (!isTodayInWeek) return { total: 0, done: 0, pct: 0, qTarget: 0, qDone: 0, qPct: 0 };
    let total = 0, done = 0, qTarget = 0, qDone = 0;
    for (const g of goals) {
      if (!g.goal_date) continue;
      if (String(g.goal_date).slice(0, 10) !== todayIso) continue;
      total++;
      const achieved = g.achieved_value ?? 0;
      const target = g.target_value ?? 0;
      if (target > 0 && achieved >= target) done++;
      if (target > 0) { qTarget += target; qDone += Math.min(achieved, target); }
    }
    const dow = (new Date(`${todayIso}T12:00:00`).getDay() + 6) % 7;
    for (const e of allEntries) {
      if (e.day_of_week !== dow) continue;
      total++;
      if (attended[todayIso]?.has(String(e.id))) done++;
    }
    return {
      total, done,
      pct: total > 0 ? Math.round((done / total) * 100) : 0,
      qTarget, qDone,
      qPct: qTarget > 0 ? Math.round((qDone / qTarget) * 100) : 0,
    };
  }, [goals, allEntries, attended, todayIso, isTodayInWeek]);

  if (childLoading) {
    return <div className="flex justify-center py-16"><div className="w-8 h-8 border-4 border-[#3973bf] border-t-transparent rounded-full animate-spin" /></div>;
  }

  return (
    <div>
      <ChildSelectionModal />

      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <h1 style={{ ...F, fontSize: 24, fontWeight: 600, color: '#2b2b2b' }}>Öğrenci Çalışma Planı</h1>
        {selectedChild && (
          <span style={{ ...F, fontSize: 16, fontWeight: 500, color: '#3973bf' }}>
            - {selectedChild.name} {selectedChild.surname}
            {selectedChild.className && ` · ${selectedChild.className}`}
          </span>
        )}
        <ChildSwitcherButton />
      </div>

      {!selectedChild ? (
        <p style={{ ...F, fontSize: 14, color: '#666' }} className="text-center py-10">Önce bir öğrenci seçin.</p>
      ) : loading ? (
        <div className="flex justify-center py-16"><div className="w-8 h-8 border-4 border-[#3973bf] border-t-transparent rounded-full animate-spin" /></div>
      ) : error ? (
        <div className="rounded-[12px] px-4 py-3 text-[13px] text-[#c73d74]" style={{ background: '#fdecef' }}>{error}</div>
      ) : (
        <>
          {/* Week selector (read-only navigation). */}
          <div className="bg-[#fafafa] rounded-[16px] p-3 xl:p-4 mb-4 flex items-center gap-3 flex-wrap" style={{ boxShadow: '0px 1px 4px rgba(0,0,0,0.08)' }}>
            <button onClick={() => setWeekOffset(o => o - 1)} aria-label="Önceki hafta" className="w-[36px] h-[36px] rounded-full hover:bg-gray-100 flex items-center justify-center">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#2b2b2b" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M15 6l-6 6 6 6" /></svg>
            </button>
            <div className="text-center flex-1 min-w-[180px]">
              <p className="text-[12px] text-gray-500">
                {weekOffset === 0 ? 'Bu Hafta' : weekOffset < 0 ? `${Math.abs(weekOffset)} hafta önce` : `${weekOffset} hafta sonra`}
              </p>
              <p className="text-[15px] xl:text-[17px] font-bold text-[#2b2b2b]">{weekRangeLabel}</p>
            </div>
            <button onClick={() => setWeekOffset(o => o + 1)} aria-label="Sonraki hafta" className="w-[36px] h-[36px] rounded-full hover:bg-gray-100 flex items-center justify-center">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#2b2b2b" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M9 6l6 6-6 6" /></svg>
            </button>
            {weekOffset !== 0 && (
              <button onClick={() => setWeekOffset(0)} className="bg-[#3973bf] text-white text-[13px] font-semibold px-4 py-2 rounded-[10px] hover:bg-blue-700">
                Bu Haftaya Dön
              </button>
            )}
          </div>

          {/* İlerleme progress bars (read-only). */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
            <div className="bg-[#fafafa] rounded-[16px] p-4" style={{ boxShadow: '0px 1px 4px rgba(0,0,0,0.08)' }}>
              <div className="flex items-center justify-between mb-2">
                <span className="text-[14px] font-semibold text-[#3973bf]">Haftalık İlerleme</span>
                <span className="text-[14px] font-bold text-[#3973bf]">%{weekProgress.pct}</span>
              </div>
              <div className="w-full h-[10px] bg-[#e5e5e5] rounded-full overflow-hidden">
                <div className="h-full bg-[#3973bf]" style={{ width: `${weekProgress.pct}%` }} />
              </div>
              <p className="text-[12px] text-gray-500 mt-1">{weekProgress.done} / {weekProgress.total} tamamlandı</p>
              {weekProgress.qTarget > 0 && (
                <div className="mt-3 pt-3 border-t border-gray-200">
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="text-[12px] font-semibold text-[#3973bf]">Çözülen Soru</span>
                    <span className="text-[12px] font-bold text-[#3973bf]">%{weekProgress.qPct}</span>
                  </div>
                  <div className="w-full h-[8px] bg-[#e5e5e5] rounded-full overflow-hidden">
                    <div className="h-full bg-[#3973bf] opacity-70" style={{ width: `${weekProgress.qPct}%` }} />
                  </div>
                  <p className="text-[12px] text-gray-500 mt-1">{weekProgress.qDone} / {weekProgress.qTarget} soru çözüldü</p>
                </div>
              )}
            </div>
            <div className="bg-[#fafafa] rounded-[16px] p-4" style={{ boxShadow: '0px 1px 4px rgba(0,0,0,0.08)' }}>
              <div className="flex items-center justify-between mb-2">
                <span className="text-[14px] font-semibold text-[#e5599b]">Günlük İlerleme</span>
                <span className="text-[14px] font-bold text-[#e5599b]">%{dailyProgress.pct}</span>
              </div>
              <div className="w-full h-[10px] bg-[#e5e5e5] rounded-full overflow-hidden">
                <div className="h-full bg-[#e5599b]" style={{ width: `${dailyProgress.pct}%` }} />
              </div>
              <p className="text-[12px] text-gray-500 mt-1">
                {isTodayInWeek ? `${dailyProgress.done} / ${dailyProgress.total} tamamlandı` : 'Bugün bu haftada değil'}
              </p>
              {isTodayInWeek && dailyProgress.qTarget > 0 && (
                <div className="mt-3 pt-3 border-t border-gray-200">
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="text-[12px] font-semibold text-[#e5599b]">Çözülen Soru</span>
                    <span className="text-[12px] font-bold text-[#e5599b]">%{dailyProgress.qPct}</span>
                  </div>
                  <div className="w-full h-[8px] bg-[#e5e5e5] rounded-full overflow-hidden">
                    <div className="h-full bg-[#e5599b] opacity-70" style={{ width: `${dailyProgress.qPct}%` }} />
                  </div>
                  <p className="text-[12px] text-gray-500 mt-1">{dailyProgress.qDone} / {dailyProgress.qTarget} soru çözüldü</p>
                </div>
              )}
            </div>
          </div>

          {/* Day grid */}
          <div className="overflow-x-auto">
            <div className="min-w-[900px]">
              <div className="grid grid-cols-7 gap-2 mb-2">
                {DAY_NAMES.map((day, i) => {
                  const d = weekDays[i];
                  const isToday = toLocalIso(d) === todayIso;
                  return (
                    <div key={i} className="text-center">
                      <div className={`rounded-[12px] py-2 xl:py-2.5 ${isToday ? 'bg-[#3973bf]' : 'bg-[#e5599b]'}`}>
                        <span className="block text-[11px] xl:text-[14px] font-semibold text-white">{day}</span>
                        <span className="block text-[10px] xl:text-[12px] text-white/90">{d.getDate()} {d.toLocaleDateString('tr-TR', { month: 'short' })}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="grid grid-cols-7 gap-2">
                {DAY_NAMES.map((_, dayIdx) => {
                  const d = weekDays[dayIdx];
                  const isoDate = toLocalIso(d);
                  // Schedule entries repeat weekly; goals only show when their
                  // goal_date falls on this specific date.
                  const dayEntries = (entriesByDay[dayIdx] ?? []).filter((e) => {
                    if (!e.__isGoal) return true;
                    const goal = goals.find(g => String(g.id) === String(e.__goalId));
                    if (!goal?.goal_date) return true;
                    return String(goal.goal_date).slice(0, 10) === isoDate;
                  });
                  return (
                    <div key={dayIdx} className="space-y-2 min-h-[400px]">
                      {dayEntries.length === 0 ? (
                        <div className="bg-[#fafafa] rounded-[12px] p-3 text-center text-[12px] text-gray-400 border border-gray-100">Boş</div>
                      ) : dayEntries.map((slot, si) => {
                        const color = si % 2 === 0 ? '#3973bf' : '#e5599b';
                        const goalMeta = slot.__isGoal ? goals.find(g => String(g.id) === String(slot.__goalId)) : undefined;
                        const completed = goalMeta ? goalMeta.status === 'completed' : (attended[isoDate]?.has(String(slot.id)) ?? false);
                        return (
                          <div key={slot.id}
                            className="rounded-[16px] relative overflow-hidden flex flex-col"
                            style={{ backgroundColor: completed ? '#dff5e0' : '#fafafa', minHeight: 140, boxShadow: '0px 1px 4px rgba(0,0,0,0.08)', border: completed ? '2px solid #11D600' : '1px solid transparent' }}>
                            {completed && (
                              <div aria-label="Tamamlandı" title="Öğretmen tarafından tamamlandı olarak işaretlendi"
                                className="absolute right-1.5 bottom-2.5 z-10 w-6 h-6 rounded-full flex items-center justify-center pointer-events-none"
                                style={{ background: '#11D600', border: '2px solid #0DA600', boxShadow: '0px 1px 2px rgba(0,0,0,0.18)' }}>
                                <svg width="11" height="9" viewBox="0 0 10 8" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M1 4l3 3 5-6" /></svg>
                              </div>
                            )}
                            <div className="p-2.5 xl:p-3 flex-1 flex flex-col" style={{ paddingRight: 32 }}>
                              {goalMeta && (
                                <span className="flex items-center gap-1 self-start mb-1">
                                  <span className="inline-block rounded-full px-2 py-0.5 text-[10px] font-bold" style={{ backgroundColor: goalAuthorBadge(goalMeta).bg, color: goalAuthorBadge(goalMeta).fg, ...F }}>
                                    {goalAuthorBadge(goalMeta).text}
                                  </span>
                                  {goalCountsLms(goalMeta) && <AutoMark size={12} />}
                                </span>
                              )}
                              {(slot.title ?? slot.topic) && <p style={{ color, fontFamily: 'Inter', fontSize: 18, fontWeight: 700, wordBreak: 'break-word' }}>{(slot.title ?? slot.topic)}</p>}
                              {goalMeta
                                ? goalScopeLine(goalMeta) && <p style={{ color, fontFamily: 'Inter', fontSize: 15, fontWeight: 600 }}>{goalScopeLine(goalMeta)}</p>
                                : slot.subjects?.name && slot.subjects.name !== (slot.title ?? slot.topic) && <p style={{ color, fontFamily: 'Inter', fontSize: 15, fontWeight: 600 }}>{slot.subjects.name}</p>}
                              {/* Schedule entries show a plain "N Soru"; goal cards show the progress bar below. */}
                              {!goalMeta && (() => {
                                const q = slot.question_target ?? slot.target_questions;
                                return q != null ? <p style={{ color, fontFamily: 'Inter', fontSize: 14, fontWeight: 600 }}>{q} Soru</p> : null;
                              })()}
                              {(() => {
                                const dur = slot.duration_minutes ?? computeDuration(slot.start_time, slot.end_time);
                                if (dur == null) return null;
                                const hours = Math.round((dur / 60) * 10) / 10;
                                return <p style={{ color, fontFamily: 'Inter', fontSize: 14, fontWeight: 600 }}>{hours} Saat</p>;
                              })()}
                              {goalMeta && (() => {
                                const target = goalMeta.target_value ?? 0;
                                const achieved = goalMeta.achieved_value ?? 0;
                                const pct = target > 0 ? Math.min(100, Math.round((achieved / target) * 100)) : 0;
                                const barColor = target > 0 && achieved >= target ? '#2fae4e' : color;
                                return (
                                  <div className="mt-1">
                                    <div className="w-full h-[6px] bg-[#e0e0e0] rounded-full overflow-hidden">
                                      <div className="h-full rounded-full" style={{ width: `${pct}%`, backgroundColor: barColor }} />
                                    </div>
                                    <p style={{ color: barColor, fontFamily: 'Inter', fontSize: 12, fontWeight: 700, marginTop: 2 }}>
                                      {achieved} / {target} Soru
                                    </p>
                                  </div>
                                );
                              })()}
                              {(slot.start_time || slot.end_time) ? (
                                <p className="mt-auto" style={{ color, fontFamily: 'Inter', fontSize: 14, fontWeight: 700 }}>{fmtTimeRange(slot.start_time, slot.end_time)}</p>
                              ) : goalMeta ? (
                                <p className="mt-auto" style={{ color, fontFamily: 'Inter', fontSize: 14, fontWeight: 700 }}>Tüm gün</p>
                              ) : null}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default ParentProgram;
